import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  LogIn,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  Package,
  Truck,
  FileText,
  BadgePercent,
  Headphones,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useUserAuth } from "../context/UserAuthContext";
import { useDemoModeOptional } from "../context/DemoModeContext";
import { AuthSidePromoSlot } from "../components/auth/AuthSidePromoSlot";
import { AuthPanelCornerDecor } from "../components/auth/AuthPanelCornerDecor";
import { AuthMobileHeroAccents } from "../components/auth/AuthMobileHeroAccents";

const AVANTAGES = [
  { icon: BadgePercent, text: "Offres et tarifs adaptés à votre compte" },
  { icon: Package, text: "Suivi des commandes et du stock en temps réel" },
  { icon: Truck, text: "Retrait en agence ou livraison selon vos besoins" },
  { icon: FileText, text: "Devis, factures et documents centralisés" },
  { icon: Headphones, text: "Équipe pro à votre écoute en magasin" },
];

export default function ConnexionPage() {
  const { login, loginDemoClient, checkEmailExists, isAuthenticated } = useUserAuth();
  const demoMode = useDemoModeOptional();
  const isDemoSession = !!demoMode?.isDemoSession;
  const demoClientEnabled = !!demoMode?.demoClientEnabled && isDemoSession;
  const demoClientEmail = demoMode?.demoClientEmail;
  const demoClientPasswordHint = demoMode?.demoClientPassword;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  const [demoPassword, setDemoPassword] = useState("");
  const [loadingDemo, setLoadingDemo] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const q = searchParams.get("email");
    if (q) {
      try {
        setEmail(decodeURIComponent(q.trim()));
      } catch {
        setEmail(q.trim());
      }
      setStep("email");
      setPassword("");
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) navigate("/compte", { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!demoClientEnabled) return;
    if (demoClientEmail) setDemoEmail(demoClientEmail);
    if (demoClientPasswordHint) setDemoPassword(demoClientPasswordHint);
  }, [demoClientEnabled, demoClientEmail, demoClientPasswordHint]);

  const handleContinueEmail = async (e) => {
    e.preventDefault();
    if (isDemoSession) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Indiquez votre adresse e-mail.");
      return;
    }
    setChecking(true);
    try {
      const exists = await checkEmailExists(trimmed);
      if (exists) {
        setStep("password");
        setPassword("");
      } else {
        navigate(`/inscription?email=${encodeURIComponent(trimmed)}`, { replace: false });
      }
    } catch (err) {
      toast.error(err.message || "Vérification impossible.");
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isDemoSession) return;
    if (!password) {
      toast.error("Saisissez votre mot de passe.");
      return;
    }
    setLoadingLogin(true);
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie.");
      navigate("/compte", { replace: true });
    } catch (err) {
      toast.error(err.message || "Connexion impossible.");
    } finally {
      setLoadingLogin(false);
    }
  };

  const goModifyEmail = () => {
    setStep("email");
    setPassword("");
  };

  const connexionFooter = (() => {
    if (isDemoSession) {
      return (
        <>
          En démonstration, utilisez le bloc <strong className="text-zinc-700">Compte client démonstration</strong>{" "}
          ci-dessus. La création de compte et le flux classique sont désactivés.
        </>
      );
    }
    if (step === "email") {
      return "Vous avez déjà un compte ? Utilisez la même adresse e-mail pour vous connecter.";
    }
    return (
      <Link to="/inscription" className="font-medium text-[#190276] hover:underline">
        Créer un autre compte
      </Link>
    );
  })();

  const handleDemoClient = async (e) => {
    e.preventDefault();
    if (!demoEmail.trim() || !demoPassword) {
      toast.error("Renseignez l’e-mail et le mot de passe démo.");
      return;
    }
    setLoadingDemo(true);
    try {
      await loginDemoClient(demoEmail.trim(), demoPassword);
      toast.success("Connexion au compte démonstration.");
      navigate("/compte", { replace: true });
    } catch (err) {
      toast.error(err.message || "Connexion démo impossible.");
    } finally {
      setLoadingDemo(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-zinc-100">
      {/* ⅔ violet + ⅓ blanc (même largeur max que le reste du site) */}
      <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-[2fr_1fr]">
        {/* Colonne violette : pleine largeur sur mobile (esprit desktop), ⅔ sur lg+ */}
        <aside className="relative order-2 flex flex-col overflow-hidden bg-gradient-to-br from-[#190276] via-[#1a0a6e] to-[#0c0438] text-white lg:order-1 lg:min-h-screen">
          <AuthPanelCornerDecor />
          <div
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 80% 60% at 100% 0%, rgba(242,181,49,0.2), transparent)",
            }}
            aria-hidden
          />
          <div className="relative z-[2] flex flex-col px-4 pb-8 pt-6 md:px-6 lg:min-h-screen lg:px-8 lg:pb-[calc(88px+1.25rem)] lg:pt-[calc(88px+1.25rem)] xl:px-10">
            <div className="hidden shrink-0 rounded-2xl border border-white/15 bg-[#0c0438]/65 px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur-md lg:block">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/90 drop-shadow-sm">
                Espace client
              </p>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-white drop-shadow-sm xl:text-3xl">
                Chauffage, plomberie, sanitaire &amp; piscines
              </h2>
              <p className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white lg:text-lg">
                Commandez et suivez vos dossiers depuis votre espace en ligne.
              </p>
            </div>
            <AuthSidePromoSlot compact />
            <ul className="mt-6 space-y-2.5 lg:mt-10 lg:space-y-3">
              {AVANTAGES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex gap-2.5 text-xs text-white lg:gap-3 lg:text-sm">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/15 text-[#F2B531] shadow-sm lg:h-8 lg:w-8">
                    <Icon className="h-3.5 w-3.5 lg:h-4 lg:w-4" strokeWidth={2} />
                  </span>
                  <span className="leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 text-xs text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
              <span className="inline-block rounded-md bg-[#0c0438]/70 px-2 py-1 backdrop-blur-sm">
                © {new Date().getFullYear()} EPP
              </span>
            </p>
          </div>
        </aside>

        {/* Colonne formulaire */}
        <div className="order-1 flex min-w-0 flex-col border-zinc-200/80 bg-zinc-100 lg:order-2 lg:border-l lg:bg-white">
          <div className="flex flex-1 flex-col px-4 pb-8 md:px-6 lg:justify-center lg:px-6 lg:py-12 xl:px-8">
            <div className="relative -mx-4 overflow-hidden bg-gradient-to-br from-[#190276] via-[#1a0a6e] to-[#0c0438] px-4 pb-8 pt-[max(0.75rem,env(safe-area-inset-top))] lg:hidden">
              <AuthMobileHeroAccents />
              <div className="relative z-[1] min-h-[4.5rem] pl-[3.25rem] text-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/75">
                  Espace client
                </p>
                <h2 className="mt-2 font-heading text-lg font-bold leading-snug">
                  Chauffage, plomberie, sanitaire &amp; piscines
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-white opacity-90">
                  Commandez et suivez vos dossiers depuis votre espace en ligne.
                </p>
              </div>
            </div>
            <div className="-mt-5 flex-1 rounded-t-[1.75rem] bg-white px-4 pb-8 pt-6 shadow-[0_-8px_32px_rgba(0,0,0,0.08)] ring-1 ring-zinc-200/90 lg:mt-0 lg:flex lg:flex-col lg:justify-center lg:rounded-none lg:bg-transparent lg:px-0 lg:pb-10 lg:pt-0 lg:shadow-none lg:ring-0">
            <div className="w-full max-w-none">
              <AnimatePresence mode="wait">
                {step === "email" ? (
                  <motion.div
                    key="email"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
                      Connexion ou inscription
                    </h1>
                    <p className="mt-2 text-[15px] leading-relaxed text-zinc-500">
                      Entrez votre e-mail : nous vous dirigerons vers la connexion ou la création de
                      compte.
                    </p>
                    {isDemoSession && (
                      <p className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50 px-3 py-2 text-[13px] text-amber-950">
                        Mode démonstration : utilisez le bloc <strong>Compte client démonstration</strong> ci-dessous. Le flux classique est désactivé.
                      </p>
                    )}

                    <form onSubmit={handleContinueEmail} className="mt-8 space-y-6">
                      <div>
                        <label
                          htmlFor="flow-email"
                          className="mb-2 block text-sm font-medium text-zinc-700"
                        >
                          Adresse e-mail <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="flow-email"
                            type="email"
                            autoComplete="email"
                            placeholder="vous@exemple.fr"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            readOnly={isDemoSession}
                            className={`h-12 rounded-xl border-zinc-200 bg-zinc-50/80 pl-11 text-[15px] focus-visible:border-[#190276] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#190276]/15 ${isDemoSession ? "cursor-not-allowed opacity-60" : ""}`}
                            required
                          />
                        </div>
                      </div>

                      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-zinc-300 sm:w-auto"
                          disabled={isDemoSession}
                          onClick={() => navigate(-1)}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          disabled={checking || isDemoSession}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160] sm:w-auto"
                        >
                          {checking ? (
                            "Vérification…"
                          ) : (
                            <>
                              Continuer
                              <ArrowRight className="h-4 w-4" strokeWidth={2} />
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="password"
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
                      Saisissez votre mot de passe
                    </h1>
                    <p className="mt-2 text-[15px] text-zinc-500">Content de vous revoir.</p>

                    <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-emerald-200/90 bg-emerald-50/90 px-4 py-3">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                          <Check className="h-4 w-4" strokeWidth={2.5} />
                        </span>
                        <span className="truncate text-sm font-medium text-emerald-950">
                          {email.trim()}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={goModifyEmail}
                        disabled={isDemoSession}
                        className="shrink-0 text-sm font-semibold text-[#190276] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                      >
                        Modifier
                      </button>
                    </div>

                    <form onSubmit={handleLogin} className="mt-6 space-y-5">
                      <div>
                        <label
                          htmlFor="flow-password"
                          className="mb-2 block text-sm font-medium text-zinc-700"
                        >
                          Mot de passe <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="flow-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            readOnly={isDemoSession}
                            className={`h-12 rounded-xl border-zinc-200 bg-zinc-50/80 pl-11 pr-12 text-[15px] focus-visible:border-[#190276] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#190276]/15 ${isDemoSession ? "cursor-not-allowed opacity-60" : ""}`}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#190276]/25"
                            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          >
                            {showPassword ? (
                              <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
                            ) : (
                              <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="text-right">
                        <Link
                          to={
                            email.trim()
                              ? `/mot-de-passe-oubli?email=${encodeURIComponent(email.trim())}`
                              : "/mot-de-passe-oubli"
                          }
                          className="text-sm font-semibold text-[#190276] underline-offset-2 hover:underline"
                        >
                          Mot de passe oublié ?
                        </Link>
                      </div>

                      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-zinc-300 sm:w-auto"
                          disabled={isDemoSession}
                          onClick={goModifyEmail}
                        >
                          Retour
                        </Button>
                        <Button
                          type="submit"
                          disabled={loadingLogin || isDemoSession}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160] sm:w-auto"
                        >
                          {loadingLogin ? (
                            "Connexion…"
                          ) : (
                            <>
                              <LogIn className="h-4 w-4" strokeWidth={2} />
                              Connexion
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {demoClientEnabled && (
                <div className="mt-8 overflow-hidden rounded-2xl border border-[#190276]/20 bg-gradient-to-br from-white via-[#f8f7ff] to-[#eef0fb] shadow-[0_12px_40px_-12px_rgba(25,2,118,0.18)]">
                  <div className="border-b border-[#190276]/10 bg-gradient-to-r from-[#190276] to-[#2a1890] px-4 py-3 sm:px-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#F2B531]">
                      Compte client démonstration
                    </p>
                    <p className="mt-1 text-xs leading-snug text-white/90">
                      Données fictives. Le mot de passe est le même que celui communiqué par <strong className="text-white">e-mail</strong> pour cet accès.
                    </p>
                  </div>
                  <form onSubmit={handleDemoClient} className="space-y-3 px-4 py-4 sm:px-5">
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold text-gray-600">E-mail</label>
                      <Input
                        type="email"
                        autoComplete="username"
                        value={demoEmail}
                        onChange={(e) => setDemoEmail(e.target.value)}
                        className="h-11 rounded-xl border-gray-200 bg-white text-sm shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                        Mot de passe (identique à celui reçu par e-mail)
                      </label>
                      <Input
                        type="text"
                        autoComplete="off"
                        value={demoPassword}
                        onChange={(e) => setDemoPassword(e.target.value)}
                        className="h-11 rounded-xl border-gray-200 bg-white font-mono text-sm shadow-sm"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loadingDemo || !demoEmail.trim() || !demoPassword}
                      className="h-12 w-full rounded-xl bg-[#190276] text-base font-semibold shadow-md shadow-[#190276]/25 hover:bg-[#130160]"
                    >
                      {loadingDemo ? (
                        "Connexion…"
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-5 w-5" strokeWidth={2} />
                          Entrer en tant que client démo
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}

              <p className="mt-10 text-center text-sm text-zinc-500">{connexionFooter}</p>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
